/* eslint-disable no-nested-ternary */
import { useCallback } from "react";
import { logger } from "../../../utils/logger";
import currency from "currency.js";
import {
  InstrumentTypeResponse,
  OrderTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { ICreateOrderBody } from "../../../interfaces/Order";
import { IStat } from "../../../interfaces/Toast";
import { AssetResponse } from "../../../utils/asset";
import { getAssetLogo } from "../../../utils/asset/assets";
import { getContractPriceStep } from "../../../utils/instruments";
import { getStopOrderName } from "../../../utils/order";
import {
  ToastEnum,
  ToastStatusEnum,
  getToastTitleForInstrument,
} from "../../../utils/toast";
import { usePerpsTradeFormFees } from "./usePerpsTradeFormFees";

export function usePerpsTradeFormCreateOrder(
  ctx: ReturnType<typeof usePerpsTradeFormFees>
) {
  const {
    perpInstrument,
    createOrder,
    commonFormTranslations,
    contractPriceStep,
    perpTradeFormTranslations,
    orderDirection,
    addToast,
    t,
    showOrderCreatedAnimation,
    mutateFarmData,
    form,
    resetAmountInput,
    onClose,
    addErrorToast,
    apiError,
    setIsLoading,
  } = ctx;

  const onCreateOrder = useCallback(
    async (order: ICreateOrderBody) => {
      if (!perpInstrument) {
        return;
      }

      try {
        setIsLoading(true);
        logger.debug("[ORDER]", order);
        const response = await createOrder(order);

        if (response.order_id) {
          const stats: IStat[] = response.stop
            ? [
                {
                  label: commonFormTranslations("contracts"),
                  value: Number(response.amount).toFixed(
                    contractPriceStep.amount_precision
                  ),
                },
                {
                  label: perpTradeFormTranslations("trigger_price"),
                  value: currency(response.trigger || 0, {
                    precision:
                      getContractPriceStep(perpInstrument).price_precision,
                  }).format(),
                },
              ]
            : [
                {
                  label:
                    orderDirection === SideResponse.Buy
                      ? commonFormTranslations("buy_amount")
                      : commonFormTranslations("sell_amount"),
                  value: (
                    <span
                      style={{
                        color:
                          orderDirection === SideResponse.Buy
                            ? COLORS.up.one
                            : COLORS.down.one,
                      }}
                    >
                      {Number(order.amount).toFixed(
                        contractPriceStep.amount_precision
                      ) || (0).toFixed(contractPriceStep.amount_precision)}
                    </span>
                  ),
                },
              ];

          // Add limit price for limit orders,
          // Add reduce only for market orders
          if (order.orderType === OrderTypeResponse.Limit) {
            stats.push({
              label: commonFormTranslations("limit_price"),
              value: currency(order.price || 0, {
                precision: getContractPriceStep(perpInstrument).price_precision,
              }).format(),
            });
          } else {
            stats.push({
              label: perpTradeFormTranslations("reduce_only"),
              value: order.reduceOnly ? "Yes" : "No",
            });
          }

          // Only show immediate toast if is market order.
          // Else just show order placed
          addToast(
            {
              type: ToastEnum.INFO,
              icon: getAssetLogo(
                perpInstrument.underlying_asset as AssetResponse
              ) as string,
              header: (
                <p>
                  {getToastTitleForInstrument(
                    InstrumentTypeResponse.Perpetual,
                    perpInstrument.instrument_name
                  )}
                </p>
              ),
              subheader: (
                <span
                  style={{
                    color:
                      orderDirection === SideResponse.Buy
                        ? COLORS.up.one
                        : COLORS.down.one,
                  }}
                >
                  {response.stop
                    ? `${response.side} ${getStopOrderName(
                        t,
                        response.order_type,
                        response.stop
                      )} - Placed`
                    : `${response.order_type} ${response.side}`}
                </span>
              ),
              stats,
              status: ToastStatusEnum.SUCCESS,
            },
            4000
          );
          showOrderCreatedAnimation();
          mutateFarmData();
          form.reset();
          resetAmountInput();
          onClose?.();
        }
      } catch (error: any) {
        let errorTitle = "";
        logger.error("[MARKET_SELL_FAILED_2]", error);
        if (order.orderType === OrderTypeResponse.Market) {
          errorTitle =
            orderDirection === SideResponse.Buy
              ? commonFormTranslations("market_buy_failed")
              : commonFormTranslations("market_sell_failed");
        } else {
          errorTitle =
            orderDirection === SideResponse.Buy
              ? commonFormTranslations("limit_buy_failed")
              : commonFormTranslations("limit_sell_failed");
        }
        addErrorToast(
          <p>{errorTitle}</p>,
          apiError(error.message) || commonFormTranslations("place_order_again")
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      perpInstrument,
      createOrder,
      commonFormTranslations,
      contractPriceStep.amount_precision,
      perpTradeFormTranslations,
      orderDirection,
      addToast,
      t,
      showOrderCreatedAnimation,
      mutateFarmData,
      form,
      resetAmountInput,
      onClose,
      addErrorToast,
      apiError,
    ]
  );

  return {
    ...ctx,
    onCreateOrder,
  };
}
